import React, { useEffect } from 'react';

import { Button, Collapse, Form, Input, Space, Spin, Typography } from 'antd';
import { Store } from 'antd/es/form/interface';
import {
  CaretRightOutlined,
  PlusOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';

import { VALIDATION_ERROR_MESSAGES } from 'src/constants';
import { MediaUploader } from 'src/ui';

import { UploadedMediaType } from 'src/ui/MediaUploader/types';

import styles from '../../Publications.module.css';
import { ApiPublicationType } from '../../types';

type FormViewProps = {
  onSubmit?: (poll: any) => void;
  loading?: boolean;
  disableForm?: boolean;
  poll?: ApiPublicationType;
};

const { Panel } = Collapse;
const { Title } = Typography;

const requiredValidationRule = {
  required: true,
  message: VALIDATION_ERROR_MESSAGES.required,
};

export const FormView: React.FC<FormViewProps> = ({
  onSubmit,
  loading,
  disableForm,
  poll,
}) => {
  const [form] = Form.useForm();

  const initialValues = {
    text: poll?.text || '',
    firstChoice: poll?.choices && poll?.choices[0] ? poll?.choices[0].text : '',
    secondChoice:
      poll?.choices && poll?.choices[1] ? poll?.choices[1].text : '',
    otherChoices: poll?.choices
      ? poll.choices.slice(2, poll.choices.length).map(choice => choice.text)
      : [],
    medias: poll?.medias
      ? poll.medias.map((media: any) => ({
          id: media.id,
          name: media.name,
          status: 'done',
          url: media.url?.original,
        }))
      : [],
  };

  const submitForm = async (values: Store) => {
    const dataToPost = {
      text: values.text,
      choices: [
        values.firstChoice,
        values.secondChoice,
        ...values.otherChoices?.filter((choice: string) => !!choice),
      ].map((choice: string) => ({ text: choice })),
      label: 'poll',
      medias: values.medias
        ? values.medias.map((media: UploadedMediaType) => ({
            id: media.id,
          }))
        : [],
      publisher: {
        id: process.env.REACT_APP_AUTOMATION_PUBLISHER_ID,
        model_type: 'user',
      },
    };

    onSubmit && onSubmit(dataToPost);
  };

  useEffect(() => {
    !loading && form.resetFields();
  }, [loading, form]);

  return poll && loading ? (
    <Spin size="large" />
  ) : (
    <Form
      id="eventForm"
      form={form}
      onFinish={submitForm}
      disabled={disableForm}
      initialValues={initialValues}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        <Title level={3} style={{ marginTop: '5px' }}>
          Créer un sondage
        </Title>
        <Collapse
          bordered={false}
          expandIconPosition="right"
          expandIcon={({ isActive }) => (
            <CaretRightOutlined rotate={isActive ? 90 : 0} />
          )}
        >
          <Panel
            id="descriptionPanel"
            forceRender={true}
            header={
              <>
                <div className={styles.circle}>1</div>
                <span>Rédigez votre message</span>
              </>
            }
            key="text"
          >
            <Form.Item
              name="text"
              rules={[
                {
                  required: true,
                  message: VALIDATION_ERROR_MESSAGES.required,
                },
                {
                  max: 400,
                  message: VALIDATION_ERROR_MESSAGES.len,
                },
              ]}
            >
              <Input.TextArea
                id="message"
                placeholder="Message (maximum 400 caractères)"
              />
            </Form.Item>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <Form.Item
                name="firstChoice"
                rules={[requiredValidationRule]}
                style={{ width: '80%' }}
              >
                <Input placeholder="Réponse 1" id="firstChoice" />
              </Form.Item>
              {poll && (
                <span style={{ color: 'green' }}>
                  {`Score: ${poll?.choices[0].score}`}
                </span>
              )}
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <Form.Item
                name="secondChoice"
                rules={[requiredValidationRule]}
                style={{ width: '80%' }}
              >
                <Input placeholder="Réponse 2" id="secondChoice" />
              </Form.Item>
              {poll && (
                <span style={{ color: 'green' }}>
                  Score: {poll?.choices[1].score}
                </span>
              )}
            </div>
            <Form.List key="otherChoices" name="otherChoices">
              {(fields, { add, remove }, { errors }) => (
                <>
                  {fields.map((field, index) => (
                    <Form.Item
                      required={false}
                      key={field.key}
                      style={{ marginBottom: '0' }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          width: '100%',
                        }}
                      >
                        <Form.Item {...field} style={{ width: '80%' }}>
                          <Input placeholder={`Réponse ${index + 3}`} />
                        </Form.Item>
                        {poll && (
                          <span style={{ color: 'green' }}>
                            Score: {poll?.choices[index + 2].score}
                          </span>
                        )}
                      </div>

                      {!poll && (
                        <MinusCircleOutlined
                          style={{
                            color: 'red',
                            fontSize: '22px',
                            position: 'absolute',
                            top: '5px',
                            right: '8px',
                          }}
                          onClick={() => remove(field.name)}
                        />
                      )}
                    </Form.Item>
                  ))}
                  {!poll && fields.length < 3 && (
                    <Form.Item>
                      <Button
                        id="add_an_anwser"
                        type="link"
                        onClick={() => add()}
                        style={{ padding: '0' }}
                        icon={<PlusOutlined />}
                      >
                        Ajouter une réponse
                      </Button>
                      <Form.ErrorList errors={errors} />
                    </Form.Item>
                  )}
                </>
              )}
            </Form.List>
            <Form.Item key="medias" name="medias">
              <MediaUploader
                limit={1}
                placeholder="Télécharger des photos ou des documents"
                pictureListType="picture-card"
                defaultValue={initialValues.medias}
                canRemove
                onUpload={(medias: Array<UploadedMediaType>) => {
                  form.setFieldsValue({
                    medias,
                  });
                }}
              >
                <Button id="media" icon={<PlusOutlined />}></Button>
              </MediaUploader>
            </Form.Item>
          </Panel>
        </Collapse>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            id="submit"
            htmlType="submit"
            type="primary"
            loading={loading}
          >
            Publier
          </Button>
        </div>
      </Space>
    </Form>
  );
};
